import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { userService, organizationService } from '../api';
import { Button, Input, ThemedText, Pagination, ActionMenu, ConfirmDialog, Avatar, SkeletonTable, EmptyState, ErrorState } from '../components';
import { UserRole, getAllRoles, getAvailableRoles, getRoleName } from '../constants/roles';
import { usePagination } from '../hooks/usePagination';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay as Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { StatusBadge } from '../components/Badge';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { Search, Users, X } from 'lucide-react';
import { FormGroup, FormLabel as Label, StyledSelect as Select } from '../components/FormStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';

const RoleBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => {
    if (props.role === 0) return '#ff6b6b20';
    if (props.role === 1) return '#4dabf720';
    return '#51cf6620';
  }};
  color: ${props => {
    if (props.role === 0) return '#ff6b6b';
    if (props.role === 1) return '#4dabf7';
    return '#51cf66';
  }};
`;

export const UsersSection = () => {
  const theme = useTheme();
  const { isSuperAdmin, isAdmin, getOrganizationId, userRole } = useRole();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState('');

  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    organizationId: '',
    userRole: UserRole.USER,
    phone: '',
    profilePicture: '',
  });

  useEffect(() => {
    fetchData();
  }, [includeInactive, selectedOrgFilter, currentPage]);

  // Reset to page 1 on filter change
  useEffect(() => {
    resetPage();
  }, [includeInactive, selectedOrgFilter, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      // Fetch users
      const params = { includeInactive, ...paginationParams };

      // If admin, only show their organization's users
      if (isAdmin() && !isSuperAdmin()) {
        params.organizationId = getOrganizationId();
      } else if (isSuperAdmin() && selectedOrgFilter) {
        params.organizationId = parseInt(selectedOrgFilter);
      }

      const usersResponse = await userService.getUsers(params);

      const { items: usersData, totalCount: total } = extractPagedData(usersResponse);
      setUsers(usersData);
      setTotalCount(total);

      // Fetch organizations (for Super Admin only)
      if (isSuperAdmin()) {
        const orgsResponse = await organizationService.getOrganizations({ includeInactive: false });
        let orgsData = [];
        if (orgsResponse.$values) {
          orgsData = orgsResponse.$values;
        } else if (Array.isArray(orgsResponse)) {
          orgsData = orgsResponse;
        } else if (orgsResponse.data) {
          orgsData = Array.isArray(orgsResponse.data) ? orgsResponse.data : orgsResponse.data.$values || [];
        }
        setOrganizations(orgsData);
      } else if (isAdmin()) {
        // For admins, get their organization
        const orgId = getOrganizationId();
        if (orgId) {
          const org = await organizationService.getOrganizationById(orgId);
          setOrganizations([org]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load users. Please check your connection and try again.');
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // If not super admin, force organization ID to current user's org
    const userData = { ...formData };
    if (!isSuperAdmin()) {
      userData.organizationId = getOrganizationId();
    } else {
      userData.organizationId = parseInt(formData.organizationId);
    }

    const validation = userService.validateCreateUserData(userData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    try {
      await userService.createUser(userData);
      toast.success('User created successfully');
      setIsCreateModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    const updateData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      profilePicture: formData.profilePicture,
    };

    const validation = userService.validateProfileData(updateData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    try {
      await userService.updateUser(selectedUser.id, updateData);
      toast.success('User updated successfully');
      setIsEditModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();

    try {
      await userService.updateUserRole(selectedUser.id, { userRole: parseInt(formData.userRole) });
      toast.success('User role updated successfully');
      setIsRoleModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update user role:', error);
      toast.error(error.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.isActive) {
        await userService.deactivateUser(user.id);
        toast.success('User deactivated');
      } else {
        await userService.activateUser(user.id);
        toast.success('User activated');
      }
      fetchData();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await userService.deleteUser(id);
      toast.success('User deleted successfully');
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      profilePicture: user.profilePicture || '',
    });
    setIsEditModalOpen(true);
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      userRole: user.userRole !== undefined ? user.userRole : user.role,
    });
    setIsRoleModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      organizationId: organizations.length > 0 ? organizations[0].id : '',
      userRole: UserRole.USER,
      phone: '',
      profilePicture: '',
    });
    setSelectedUser(null);
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin()) {
    return (
      <PageContainer theme={theme}>
        <ThemedText type="title">Access Denied</ThemedText>
        <ThemedText>You do not have permission to access this page.</ThemedText>
      </PageContainer>
    );
  }

  return (
    <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <div>
            <ThemedText type="title">Users</ThemedText>
            <ThemedText type="subtitle">
              {isSuperAdmin() ? 'Manage all users' : 'Manage users in your organization'}
            </ThemedText>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            + Create User
          </Button>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer theme={theme}>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>
          {isSuperAdmin() && organizations.length > 0 && (
            <Select
              value={selectedOrgFilter}
              onChange={(e) => setSelectedOrgFilter(e.target.value)}
              theme={theme}
            >
              <option value="">All Organizations</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </Select>
          )}
          <ToggleSwitch theme={theme}>
            <ToggleInput
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <ToggleSlider theme={theme} $checked={includeInactive} />
            <ThemedText type="defaultSemiBold">Show inactive</ThemedText>
          </ToggleSwitch>
        </SearchContainer>

        <TableContainer theme={theme}>
          {loading ? (
            <SkeletonTable rows={5} cols={6} theme={theme} />
          ) : fetchError ? (
            <ErrorState
              theme={theme}
              title="Failed to load users"
              message={fetchError}
              onRetry={fetchData}
            />
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              theme={theme}
              icon={<Users size={48} />}
              title="No users found"
              description={searchTerm ? 'Try adjusting your search term' : 'Invite team members to get started'}
            />
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>User</Th>
                  <Th theme={theme}>Email</Th>
                  <Th theme={theme}>Role</Th>
                  {isSuperAdmin() && <Th theme={theme}>Organization</Th>}
                  <Th theme={theme}>Status</Th>
                  <Th theme={theme} style={{ width: '50px' }}></Th>
                </tr>
              </Thead>
              <Tbody>
                {filteredUsers.map(user => (
                  <Tr key={user.id} theme={theme}>
                    <Td theme={theme}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar src={user.profilePicture} name={`${user.firstName} ${user.lastName}`} size="sm" />
                        <div>
                          <ThemedText type="defaultSemiBold">
                            {user.firstName} {user.lastName}
                          </ThemedText>
                          {user.phone && (
                            <ThemedText type="small" style={{ color: theme.colors.muted }}>
                              {user.phone}
                            </ThemedText>
                          )}
                        </div>
                      </div>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{user.email}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <RoleBadge theme={theme} role={user.userRole !== undefined ? user.userRole : user.role}>
                        {getRoleName(user.userRole !== undefined ? user.userRole : user.role)}
                      </RoleBadge>
                    </Td>
                    {isSuperAdmin() && (
                      <Td theme={theme}>
                        <ThemedText type="small">
                          {organizations.find(org => org.id === user.organizationId)?.name || 'N/A'}
                        </ThemedText>
                      </Td>
                    )}
                    <Td theme={theme}>
                      <StatusBadge theme={theme} isActive={user.isActive}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </Td>
                    <Td theme={theme}>
                      <ActionMenu actions={[
                        { label: 'Edit', onClick: () => openEditModal(user) },
                        { label: 'Change Role', onClick: () => openRoleModal(user) },
                        { label: user.isActive ? 'Deactivate' : 'Activate', onClick: () => handleToggleActive(user) },
                        { label: 'Delete', onClick: () => setDeleteConfirmId(user.id), variant: 'danger' },
                      ]} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={goToPage}
            itemLabel="users"
          />
        )}

        {/* Create Modal */}
        <Modal theme={theme} isOpen={isCreateModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Create User</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleCreateUser}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Email *</Label>
                  <Input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>First Name *</Label>
                  <Input
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Last Name *</Label>
                  <Input
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Password *</Label>
                  <Input
                    required
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                  />
                </FormGroup>

                {isSuperAdmin() && (
                  <FormGroup theme={theme}>
                    <Label theme={theme}>Organization *</Label>
                    <Select
                      required
                      value={formData.organizationId}
                      onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                      theme={theme}
                    >
                      <option value="">Select organization</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </Select>
                  </FormGroup>
                )}

                <FormGroup theme={theme}>
                  <Label theme={theme}>Role *</Label>
                  <Select
                    required
                    value={formData.userRole}
                    onChange={(e) => setFormData({ ...formData, userRole: parseInt(e.target.value) })}
                    theme={theme}
                  >
                    {getAvailableRoles(userRole).map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
              </ModalBody>

              <ModalFooter theme={theme}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setIsCreateModalOpen(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create User
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Edit Modal */}
        <Modal theme={theme} isOpen={isEditModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Edit User</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsEditModalOpen(false); resetForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateUser}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>First Name</Label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Last Name</Label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
              </ModalBody>

              <ModalFooter theme={theme}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setIsEditModalOpen(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update User
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Role Modal */}
        <Modal theme={theme} isOpen={isRoleModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Change User Role</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsRoleModalOpen(false); resetForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateRole}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Role</Label>
                  <Select
                    value={formData.userRole}
                    onChange={(e) => setFormData({ ...formData, userRole: parseInt(e.target.value) })}
                    theme={theme}
                  >
                    {getAvailableRoles(userRole).map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </Select>
                </FormGroup>
              </ModalBody>

              <ModalFooter theme={theme}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setIsRoleModalOpen(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update Role
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone."
          onConfirm={() => handleDeleteUser(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
    </PageContainer>
  );
};

export const UsersPage = () => (
  <Layout currentPath="/settings">
    <UsersSection />
  </Layout>
);

export default UsersPage;
